import React from 'react';
import { GitHubCalendar } from 'react-github-calendar'; 
import { motion } from 'framer-motion';
import { 
  SiPython, SiJavascript, SiReact, SiSymfony, 
  SiC, SiDotnet, SiMongodb, SiMysql, 
  SiDocker, SiPostman, SiGit, 
  SiIntellijidea, SiLinux, SiWindows
} from 'react-icons/si';
import { FaJava, FaTerminal, FaMicrochip, FaDesktop, FaKeyboard, FaMouse, FaHeadphones } from 'react-icons/fa';
import { VscCode } from 'react-icons/vsc';

// Assure-toi que l'image est bien dans ce dossier
import Chibi from '../img/moi_chibi.png'; 

const Tools = () => {
  const skillset = [
    { name: "Python", icon: <SiPython className="text-[#3776AB]" />, pct: 50, color: "#3776AB" },
    { name: "JavaScript", icon: <SiJavascript className="text-[#F7DF1E]" />, pct: 75, color: "#F7DF1E" },
    { name: "React.js", icon: <SiReact className="text-[#61DAFB]" />, pct: 75, color: "#61DAFB" },
    { name: "Symfony 7 / PHP", icon: <SiSymfony className="text-[#000000] dark:text-white" />, pct: 90, color: "#5865F2" },
    { name: "Java / FX", icon: <FaJava className="text-[#007396]" />, pct: 90, color: "#007396" },
    { name: "Langage C", icon: <SiC className="text-[#A8B9CC]" />, pct: 90, color: "#A8B9CC" },
    { name: "C# / .NET", icon: <SiDotnet className="text-[#512BD4]" />, pct: 75, color: "#512BD4" },
    { name: "MySQL", icon: <SiMysql className="text-[#4479A1]" />, pct: 75, color: "#4479A1" },
    { name: "VHDL", icon: <FaMicrochip className="text-[#FFB11B]" />, pct: 20, color: "#FFB11B" }
  ];

  const workflow = [
    { name: "VS Code", icon: <VscCode className="text-[#007ACC]" /> },
    { name: "IntelliJ IDEA", icon: <SiIntellijidea className="text-[#FE315D]" /> },
    { name: "MongoDB", icon: <SiMongodb className="text-[#47A248]" /> },
    { name: "Postman", icon: <SiPostman className="text-[#FF6C37]" /> },
    { name: "Docker", icon: <SiDocker className="text-[#2496ED]" /> },
    { name: "Git", icon: <SiGit className="text-[#F05032]" /> },
    { name: "Serveur local", icon: <FaTerminal className="text-gray-500" /> }
  ];

  // Nouvelle section Hardware / Setup
  const setup = [
    { name: "OS", val: "Linux (Ubuntu)", icon: <SiLinux /> },
    { name: "Écrans", val: "Dual Monitor 27\"", icon: <FaDesktop /> },
    { name: "Périphériques", val: "Clavier Méca", icon: <FaKeyboard /> },
    { name: "Souris", val: "Logitech G502", icon: <FaMouse /> },
    { name: "Audio", val: "Casque Noise Cancelling", icon: <FaHeadphones /> },
  ];

  return (
    <div className="pt-24 pb-0 overflow-x-hidden">
      
      {/* SECTION 1 : HEADER (Centré) */}
      <section className="max-w-6xl mx-auto px-6 py-16 text-center">
        <h2 className="text-5xl font-black mb-4 italic tracking-tighter uppercase">Ma Boîte à Outils 🛠️</h2>
        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          Un arsenal polyvalent allant de l'ingénierie logicielle au développement Web full-stack.
        </p>
      </section>

      {/* SECTION 2 : SKILLSET (Bannière Grise/Noir + XP BARS + TILT 3D) */}
      <section className="w-full bg-gray-50 dark:bg-[#0c0c0c] border-y border-gray-100 dark:border-white/5 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h3 className="text-xl font-bold mb-10 text-neon-purple uppercase tracking-[0.2em] flex items-center gap-4">
            Compétences Techniques (XP) <span className="h-[2px] flex-1 bg-neon-purple/20"></span>
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {skillset.map((item, idx) => (
              <motion.div 
                key={idx} 
                // Effet Tilt 3D léger au survol
                whileHover={{ y: -5, scale: 1.02, rotateX: 5, rotateY: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="p-6 rounded-2xl bg-white dark:bg-discord-gray shadow-sm border border-transparent hover:border-neon-purple/30 group flex flex-col justify-between h-full"
              >
                <div>
                    <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300 origin-left">
                    {item.icon}
                    </div>
                    <h4 className="font-bold mb-3">{item.name}</h4>
                </div>
                
                {/* BARRE D'XP ANIMÉE */}
                <div className="w-full bg-gray-200 dark:bg-black/50 rounded-full h-2.5 mt-auto relative overflow-hidden">
                   <motion.div 
                     className="h-2.5 rounded-full"
                     style={{ backgroundColor: item.color }}
                     initial={{ width: 0 }}
                     whileInView={{ width: `${item.pct}%` }}
                     transition={{ duration: 1.5, ease: "easeOut" }}
                   />
                </div>
                <div className="flex justify-end mt-1">
                    <span className="text-[10px] font-mono opacity-60">{item.pct}% XP</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 : WORKFLOW (MODIFIÉE : Couleurs actives + Scale on Hover) */}
      <section className="w-full bg-discord-blurple/[0.03] py-20">
        <div className="max-w-6xl mx-auto px-6 text-center md:text-left">
          <h3 className="text-xl font-bold mb-10 text-discord-blurple uppercase tracking-[0.2em]">Workflow & Outils</h3>
          <div className="flex flex-wrap justify-center md:justify-start gap-6">
            {workflow.map((item, idx) => (
              <motion.div 
                key={idx} 
                // Augmente la taille (scale) au survol
                whileHover={{ scale: 1.15 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                className="flex flex-col items-center gap-2 p-4 min-w-[100px]"
              >
                {/* Plus de grayscale, couleurs d'origine directes */}
                <span className="text-5xl filter drop-shadow-lg">{item.icon}</span>
                <span className="text-xs font-bold uppercase tracking-tighter opacity-80">{item.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 : SETUP */}
      <section className="w-full bg-white dark:bg-[#080808] border-t border-gray-100 dark:border-white/5 py-16">
        <div className="max-w-5xl mx-auto px-6">
            <h3 className="text-xl font-bold mb-8 text-gray-400 uppercase tracking-widest text-center">Mon Setup de Dev</h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {setup.map((s, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-transparent hover:border-discord-blurple/30 transition-colors">
                        <span className="text-2xl text-discord-blurple">{s.icon}</span>
                        <div>
                            <p className="text-xs uppercase font-bold opacity-50">{s.name}</p>
                            <p className="font-medium text-sm">{s.val}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
      </section>

      {/* SECTION 5 : GITHUB */}
      <section className="w-full bg-white dark:bg-[#050505] py-20 border-t border-gray-100 dark:border-white/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-gray-50 dark:bg-discord-gray/30 p-10 rounded-[3rem] border border-transparent dark:border-white/5">
            <h3 className="text-2xl font-bold mb-8 flex items-center justify-center gap-3">
              Activité Open Source <span className="text-xs font-mono opacity-50 font-normal">@JeMeSuisPerdu</span>
            </h3>
            <div className="flex justify-center overflow-x-auto pb-4 custom-scrollbar">
               <GitHubCalendar 
                 username="JeMeSuisPerdu" 
                 blockSize={13} 
                 blockMargin={5} 
                 colorScheme="dark"
               />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 : PHILOSOPHIE AVEC CHIBI */}
      <section className="w-full bg-gradient-to-b from-transparent to-discord-blurple/10 py-24 border-t border-gray-50 dark:border-white/5">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-center gap-12">
          
          {/* IMAGE CHIBI */}
          <div className="relative w-48 h-48 md:w-64 md:h-64 flex-shrink-0 group">
             <div className="absolute inset-0 bg-discord-blurple/20 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
             <img 
               src={Chibi} 
               alt="Moi en version Chibi" 
               className="relative w-full h-full object-contain drop-shadow-2xl hover:scale-110 hover:-rotate-3 transition-transform duration-300"
             />
          </div>

          {/* CITATION */}
          <div className="text-center md:text-left max-w-2xl">
            <p className="text-3xl italic font-black text-discord-blurple mb-6 leading-tight relative">
              <span className="text-6xl absolute -top-6 -left-4 opacity-20 select-none">"</span>
              Un idiot qui marche ira toujours plus loin que deux intellectuels assis.
              <span className="text-6xl absolute -bottom-8 -right-4 opacity-20 select-none">"</span>
            </p>
            <p className="text-gray-500 font-medium tracking-wide text-lg">
              Je privilégie la pratique à la théorie pure pour construire des solutions concrètes.
            </p>
          </div>

        </div>
      </section>

    </div>
  );
};

export default Tools;